import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack } from '@mui/material';
import {
  applyDueSubscriptionChanges,
  applySubscriptionChange,
  cancelSubscriptionChange,
  getSubscriptionChangeRequest,
  getSubscriptionChangeRequests,
} from '../api/subscriptionChangesApi';
import { colorTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { SubscriptionChangeDetailDialog } from '../components/SubscriptionChangeDialog';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';

const { semantic } = colorTokens;

const STATUS_FILTERS = ['pending_payment', 'scheduled', 'applied', 'canceled', 'failed'];
const CHANGE_TYPE_FILTERS = ['upgrade', 'downgrade', 'renewal_change', 'override_change'];

const STATUS_I18N_KEY = {
  pending_payment: 'pendingPayment',
  scheduled: 'scheduled',
  applied: 'applied',
  canceled: 'canceled',
  failed: 'failed',
};

const CHANGE_TYPE_I18N_KEY = {
  upgrade: 'upgrade',
  downgrade: 'downgrade',
  renewal_change: 'renewalChange',
  override_change: 'overrideChange',
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const forceApplySx = {
  ...rowActionSx,
  color: semantic.warning,
  border: `1px solid ${semantic.warning}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(237, 149, 58, 0.08)',
    borderColor: semantic.warning,
    color: semantic.warning,
  },
};

function changeStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'applied' || normalized === 'success') return 'Success';
  if (normalized === 'pending_payment' || normalized === 'pending') return 'Warning';
  if (normalized === 'scheduled') return 'Info';
  if (normalized === 'failed' || normalized === 'canceled' || normalized === 'cancelled') return 'Danger';
  return 'Neutral';
}

function statusLabel(t, status) {
  const key = STATUS_I18N_KEY[status] || status;
  return t(`subscriptionChanges:${key}`, { defaultValue: status });
}

function changeTypeLabel(t, type) {
  const key = CHANGE_TYPE_I18N_KEY[type] || type;
  return t(`subscriptionChanges:${key}`, { defaultValue: type });
}

export function SubscriptionChangeRequestsPage() {
  const { t } = useTranslation(['subscriptionChanges', 'billing', 'common']);
  const { session } = useAuth();
  const canApply = hasPermission(session, 'system.billing.subscription_changes.apply');
  const canManage = hasPermission(session, 'system.billing.subscription_changes.manage');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [changeTypeFilter, setChangeTypeFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const emptyValue = t('common:empty_value');
  const recordCount = items.length;

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (changeTypeFilter) params.change_type = changeTypeFilter;

    getSubscriptionChangeRequests(params)
      .then((response) => setItems(response.data || []))
      .catch(() => setError(t('subscriptionChanges:loadFailed')))
      .finally(() => setLoading(false));
  }, [statusFilter, changeTypeFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = useCallback(async (id) => {
    try {
      const data = await getSubscriptionChangeRequest(id);
      setDetail(data);
      setDetailOpen(true);
    } catch {
      setError(t('subscriptionChanges:loadFailed'));
    }
  }, [t]);

  const handleDetailAction = async (action) => {
    if (!detail) return;
    setMessage('');
    try {
      if (action === 'cancel') {
        await cancelSubscriptionChange(detail.id);
        setMessage(t('subscriptionChanges:cancelSuccess'));
      }
      if (action === 'apply') {
        await applySubscriptionChange(detail.id, { force: true });
        setMessage(t('subscriptionChanges:applySuccess'));
      }
      setDetailOpen(false);
      load();
    } catch {
      setError(action === 'cancel' ? t('subscriptionChanges:cancelFailed') : t('subscriptionChanges:applyFailed'));
    }
  };

  const handleApplyDue = async () => {
    setMessage('');
    try {
      const result = await applyDueSubscriptionChanges();
      setMessage(t('subscriptionChanges:applyDueSuccess', { count: result.count ?? 0 }));
      load();
    } catch {
      setError(t('subscriptionChanges:applyFailed'));
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (row) => row.customer?.name || row.customer_id || emptyValue,
      },
      {
        id: 'tenant',
        label: t('billing:tenant'),
        render: (row) => row.tenant?.name || row.tenant_id || emptyValue,
      },
      {
        id: 'subscription',
        label: t('billing:subscription'),
        render: (row) => (row.subscription_id != null ? `#${row.subscription_id}` : emptyValue),
      },
      {
        id: 'change_type',
        label: t('subscriptionChanges:changeType'),
        render: (row) => (
          row.change_type
            ? changeTypeLabel(t, row.change_type)
            : emptyValue
        ),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="generic"
                apiLabel={statusLabel(t, row.status)}
                size="Sm"
                tone={changeStatusTone(row.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'effective_at',
        label: t('subscriptionChanges:effectiveAt'),
        render: (row) => (
          row.effective_at
            ? new Date(row.effective_at).toLocaleString()
            : emptyValue
        ),
      },
      {
        id: 'price_delta',
        label: t('subscriptionChanges:priceDelta'),
        render: (row) => (
          row.price_delta_amount != null
            ? `${row.price_delta_amount} ${row.currency || ''}`.trim()
            : emptyValue
        ),
      },
      {
        id: 'no_refund',
        label: t('subscriptionChanges:noRefund'),
        render: (row) => (
          <StatusBadge
            status={row.no_refund ? 'yes' : 'no'}
            category="generic"
            apiLabel={row.no_refund ? t('common:yes') : t('common:no')}
            size="Sm"
            tone={row.no_refund ? 'Warning' : 'Neutral'}
          />
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (row) => (
          <Stack
            direction="column"
            spacing={0.75}
            useFlexGap
            alignItems="flex-end"
            sx={{
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            <Button
              variant="Ghost"
              onClick={() => openDetail(row.id)}
              sx={rowActionSx}
            >
              {t('subscriptionChanges:viewDetail')}
            </Button>
            {row.invoice_id && (
              <Button
                variant="Ghost"
                component={RouterLink}
                to={`/billing/invoices/${row.invoice_id}`}
                sx={rowActionSx}
              >
                {t('subscriptionChanges:viewInvoice')}
              </Button>
            )}
            {canApply && row.status === 'pending_payment' && (
              <Button
                variant="Ghost"
                onClick={() => {
                  setError('');
                  applySubscriptionChange(row.id, { force: true })
                    .then(() => {
                      setMessage(t('subscriptionChanges:applySuccess'));
                      load();
                    })
                    .catch((err) => setError(safeApiErrorMessage(err, t('subscriptionChanges:applyFailed'))));
                }}
                sx={forceApplySx}
              >
                {t('subscriptionChanges:forceApply')}
              </Button>
            )}
            {canManage && row.status === 'scheduled' && (
              <Button
                variant="Ghost"
                onClick={() => {
                  setError('');
                  cancelSubscriptionChange(row.id)
                    .then(() => {
                      setMessage(t('subscriptionChanges:cancelSuccess'));
                      load();
                    })
                    .catch((err) => setError(safeApiErrorMessage(err, t('subscriptionChanges:cancelFailed'))));
                }}
                sx={rowActionSx}
              >
                {t('subscriptionChanges:cancelScheduledDowngrade')}
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [canApply, canManage, emptyValue, load, openDetail, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('subscriptionChanges:title')}
        subtitle={t('subscriptionChanges:subtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="Secondary" component={RouterLink} to="/billing">
              {t('common:dashboard')}
            </Button>
            {canApply && (
              <Button variant="Accent" onClick={handleApplyDue}>
                {t('subscriptionChanges:applyDue')}
              </Button>
            )}
          </Stack>
        )}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {STATUS_FILTERS.map((status) => (
            <FilterSelectOption key={status} value={status}>
              {statusLabel(t, status)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('subscriptionChanges:changeType')}
          hideLabel
          value={changeTypeFilter}
          onChange={(e) => setChangeTypeFilter(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {CHANGE_TYPE_FILTERS.map((type) => (
            <FilterSelectOption key={type} value={type}>
              {changeTypeLabel(t, type)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('subscriptionChanges:title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('subscriptionChanges:toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={items}
            getRowKey={(row) => row.id}
            emptyMessage={t('subscriptionChanges:noChanges')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <SubscriptionChangeDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        changeRequest={detail}
        onRefresh={handleDetailAction}
      />
    </Box>
  );
}
